import { useParticipationFlow } from "~/components/participation/ParticipationFlow";
import { DialogTitle } from "@headlessui/react";
import YellowFlowerImage from "~/images/yellow-flower.png";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import type { ApprovalRequestStatusResult } from "~/routes/api.events.$eventId.request-status";
import { useCheckoutSession } from "./CheckoutSession";
import {
  EventRequestResolutionReason,
  EventRequestState,
  type EventFragment,
  type EventTicketPurchaserDetailsInput,
} from "~/__generated__/graphql/graphql";
import { PrimaryButton } from "~/components/Button";
import { PlanDialog } from "~/components/plans/components/PlanDialog";
import { StepIndicator } from "~/components/plans/components/StepIndicator";
import { Text } from "~/components/Text";
import { useUser } from "~/hooks/use-user";
import { useRootEnvironment, useRootLocale } from "~/root";
import { checkoutContactEmailSchema } from "~/utils/contact-email";
import { getRegionalCopy } from "~/utils/localization";
import { ApprovalProfileStep } from "./ApprovalProfileStep";
import { ApprovalQuestionsStep } from "./ApprovalQuestionsStep";
import { approvalRequestForm } from "./approval-form";
import { CheckoutEmailStep } from "./CheckoutEmailStep";
import { CheckoutPaymentStep } from "./CheckoutPaymentStep";
import { CheckoutPhoneAuthentication } from "./CheckoutPhoneAuthentication";
import { CheckoutTicketsStep } from "./CheckoutTicketsStep";
import { CheckoutGuestsStep } from "./CheckoutGuestsStep";
import { isTicketCheckoutAvailable } from "./ticketSalesAvailability";
import { TICKET_VARIATION_PARAM } from "./ticketVariations";
import { TicketConfirmation } from "./TicketStatusDialog";


type ApprovalStep =
  | "tickets"
  | "guests"
  | "phone"
  | "profile"
  | "questions"
  | "email"
  | "payment"
  | "status";

type Props = {
  plan: EventFragment;
  authenticated: boolean;
  userExists: boolean;
  stripePublishableKey: string | undefined;
  ticketVariationsEnabled: boolean;
};

const approvalSteps = new Set<ApprovalStep>([
  "tickets",
  "guests",
  "phone",
  "profile",
  "questions",
  "email",
  "payment",
  "status",
]);

function isApprovalStep(value: string | null): value is ApprovalStep {
  return value !== null && approvalSteps.has(value as ApprovalStep);
}

export function hasTicketApprovalProfile(
  user: ReturnType<typeof useUser>["user"],
): boolean {
  if (!user) return false;
  const name = user.name.trim();
  return (
    name.length > 0 &&
    name.toLowerCase() !== "anonymous user" &&
    /\p{L}/u.test(name) &&
    !!user.phoneNumber?.trim() &&
    user.age >= 18 &&
    user.age <= 100 &&
    user.gender.trim().length > 0 &&
    (user.headline.trim().length > 0 || user.bio.trim().length > 0) &&
    user.profileImageUrl.trim().length > 0
  );
}

function nextDetailsStep(
  authenticated: boolean,
  userExists: boolean,
  hasVerifiedPhone: boolean,
  completeProfile: boolean,
): ApprovalStep {
  if (!authenticated || !userExists || !hasVerifiedPhone) return "phone";
  if (!completeProfile) return "profile";
  return "questions";
}

export function ApprovalCheckoutDialog({
  plan,
  authenticated,
  userExists,
  stripePublishableKey,
  ticketVariationsEnabled,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const checkout = useCheckoutSession();
  const { setStep, close } = checkout;
  const { user } = useUser();
  const statusScope = `${plan.id}:${user?.id ?? "anonymous"}`;
  const currentStatusScope = useRef(statusScope);
  currentStatusScope.current = statusScope;
  const statusRead = useRef<AbortController | null>(null);
  const [checkedStatus, setCheckedStatus] = useState<{
    scope: string;
    source: EventFragment;
    event: Extract<ApprovalRequestStatusResult, { ok: true }>["event"];
  }>();
  const [statusError, setStatusError] = useState<{ scope: string; message: string }>();
  const [checkingStatus, setCheckingStatus] = useState(false);
  useEffect(() => {
    currentStatusScope.current = statusScope;
    return () => {
      if (currentStatusScope.current === statusScope) currentStatusScope.current = "";
      statusRead.current?.abort();
    };
  }, [statusScope]);
  const status = checkedStatus?.scope === statusScope && checkedStatus.source === plan
    ? checkedStatus.event : undefined;
  const currentRequest = status
    ? status.myRequest && plan.myRequest ? { ...plan.myRequest, ...status.myRequest } : null
    : plan.myRequest;
  const isAttending = status?.isAttending ?? plan.isAttending;
  const ticketOutcomeSMSEnabled = useRootEnvironment()?.TICKET_OUTCOME_SMS_ENABLED;
  const needsContactEmail =
    !ticketOutcomeSMSEnabled &&
    !checkout.draft.walletIntentID &&
    !checkoutContactEmailSchema.safeParse(user?.contactEmail).success;
  const nextPaymentStep = needsContactEmail ? "email" : "payment";
  const requestedStep = searchParams.get("step");
  const step = isApprovalStep(requestedStep) ? requestedStep : null;
  const submittedRequest = currentRequest?.submittedAt ? currentRequest : null;
  const acceptedWaitlistClaim =
    submittedRequest?.state === EventRequestState.InAcceptedWaitlist;
  const detailsStep = nextDetailsStep(
    authenticated,
    userExists,
    !!user?.phoneNumber?.trim(),
    hasTicketApprovalProfile(user),
  );
  const groupSlots = checkout.draft.groupSlots ?? [];
  const isGroup = groupSlots.length > 1;
  const flow = useParticipationFlow();
  const displayedStep = flow.step;

  const checkoutAvailable = isTicketCheckoutAvailable(plan);
  const showingRequestStatus = displayedStep === "status" && authenticated && userExists;
  const staleStatus = showingRequestStatus && !submittedRequest && !isAttending;
  const open = displayedStep !== null && checkoutAvailable && !staleStatus;

  useEffect(() => {
    if (staleStatus) close();
  }, [staleStatus, close]);

  const refreshStatus = async () => {
    if (statusRead.current || !open || !showingRequestStatus) return;
    const controller = new AbortController();
    statusRead.current = controller;
    const scope = statusScope;
    setCheckingStatus(true);
    setStatusError(undefined);
    const timeout = window.setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(`/api/events/${encodeURIComponent(plan.id)}/request-status`, {
        cache: "no-store", signal: controller.signal,
      });
      const result = await response.json() as ApprovalRequestStatusResult;
      if (controller.signal.aborted || !response.ok || !result.ok || result.event.id !== plan.id ||
        result.event.myRequest && result.event.myRequest.id !== plan.myRequest?.id)
        throw new Error("Request status unavailable");
      if (currentStatusScope.current !== scope || controller.signal.aborted) return;
      setCheckedStatus({ scope, source: plan, event: result.event });
      if (result.event.isAttending) flow.complete("booking_confirmed");
    } catch {
      if (currentStatusScope.current === scope && statusRead.current === controller)
        setStatusError({ scope, message: "We couldn’t check your request. Try again." });
    } finally {
      window.clearTimeout(timeout);
      if (statusRead.current === controller) {
        statusRead.current = null;
        if (currentStatusScope.current === scope) setCheckingStatus(false);
      }
    }
  };

  // Match the instant checkout: once this flow is unavailable, retire stale
  // checkout params so a later focus revalidation can't reopen the dialog.
  useEffect(() => {
    if (step === null || checkoutAvailable) return;
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("step");
        next.delete("discountCode");
        next.delete(TICKET_VARIATION_PARAM);
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
  }, [checkoutAvailable, setSearchParams, step]);

  const reloadAtStep = (nextStep: ApprovalStep) => checkout.refreshUser(nextStep);

  return (
    <PlanDialog open={open} onClose={close} fullScreenOnMobile={!showingRequestStatus} includeLogo={!showingRequestStatus}>
      {checkout.error && <p role="alert" className="mb-3 font-sans text-sm text-butter-pink">{checkout.error} <button type="button" className="underline" onClick={() => void checkout.flush()}>Try again</button></p>}
      {displayedStep === "tickets" && (
        <CheckoutTicketsStep
          plan={plan}
          ticketVariationsEnabled={ticketVariationsEnabled}
          nextStep={isGroup ? "guests" : acceptedWaitlistClaim ? nextPaymentStep : detailsStep}
        />
      )}
      {displayedStep === "guests" && (
        <CheckoutGuestsStep plan={plan} onComplete={() => setStep(detailsStep)} />
      )}
      {displayedStep === "phone" && (
        <ApprovalDetailsStage onBack={() => setStep("tickets")}>
          <CheckoutPhoneAuthentication
            eventId={plan.id}
            onBack={flow.back}
            onComplete={() => checkout.refreshUser("questions", true)}
          />
        </ApprovalDetailsStage>
      )}
      {displayedStep === "profile" && (
        <ApprovalDetailsStage onBack={() => setStep("tickets")}>
          <ApprovalProfileStep
            eventId={plan.id}
            onBack={flow.back}
            host={plan.hosts.find(host => host.primary) ?? plan.hosts[0]}
            onComplete={() => reloadAtStep("questions")}
          />
        </ApprovalDetailsStage>
      )}
      {displayedStep === "questions" && (
        <ApprovalDetailsStage onBack={() => setStep("tickets")}>
          <ApprovalQuestionsStep
            plan={plan}
            onBack={flow.back}
            onComplete={flow.next}
          />
        </ApprovalDetailsStage>
      )}
      {displayedStep === "email" && (
        <CheckoutEmailStep
          eventId={plan.id}
          requiresApproval={!acceptedWaitlistClaim}
          onBack={flow.back}
        />
      )}
      {displayedStep === "payment" && (
        <ApprovalPaymentStep
          plan={plan}
          authenticated={authenticated}
          userExists={userExists}
          stripePublishableKey={stripePublishableKey}
          ticketVariationsEnabled={ticketVariationsEnabled}
        />
      )}
      {displayedStep === "status" && (!authenticated || !userExists) ? (
        <CheckoutPhoneAuthentication
          eventId={plan.id}
          onComplete={() => checkout.refreshUser("status", true)}
        />
      ) : (
        displayedStep === "status" && (
          <RequestStatusStep
            plan={{ ...plan, myRequest: currentRequest }}
            isAttending={isAttending}
            requiresApproval={plan.myRegistration?.status.approvalRequired ?? checkout.draft.attempt?.approvalRequired ?? plan.approveGuests}
            onRefresh={refreshStatus}
            refreshing={checkingStatus}
            error={statusError?.scope === statusScope ? statusError.message : undefined}
            onStartCheckout={() => {
              checkout.update({ walletIntentID: "", attempt: null, paymentPhase: "overview" });
              void checkout.refreshUser("tickets");
            }}
          />
        )
      )}
    </PlanDialog>
  );
}

function ApprovalDetailsStage({
  children,
  onBack,
}: {
  children: ReactNode;
  onBack: () => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0">
        <StepIndicator
          currentStep={2}
          showTicketStep
          stepActions={{ 1: onBack }}
        />
      </div>
      {children}
    </div>
  );
}

function ApprovalPaymentStep(props: Props) {
  const flow = useParticipationFlow();
  const { draft } = useCheckoutSession();
  const eventRequestID =
    props.plan.myRequest?.state === EventRequestState.InAcceptedWaitlist
      ? props.plan.myRequest.id
      : undefined;
  const purchaserDetails: EventTicketPurchaserDetailsInput = eventRequestID
    ? { eventRequestID }
    : {
        form: approvalRequestForm(props.plan.formInputs, draft.answers, props.plan.myRequest),
        ...(draft.message ? { message: draft.message } : {}),
      };

  return <CheckoutPaymentStep {...props} purchaserDetails={purchaserDetails} onBack={flow.back} />;
}

function RequestStatusStep({
  plan,
  isAttending,
  requiresApproval,
  onStartCheckout,
  onRefresh,
  refreshing,
  error,
}: {
  plan: EventFragment;
  isAttending: boolean;
  requiresApproval: boolean;
  onStartCheckout: () => void;
  onRefresh: () => Promise<void>;
  refreshing: boolean;
  error?: string;
}) {
  const regionalCopy = getRegionalCopy(useRootLocale());
  const request = plan.myRequest;
  if (isAttending) {
    return (
      <div className="flex flex-1 flex-col pt-8 text-center">
        <TicketConfirmation
          planTitle={plan.title}
          requiresApproval={requiresApproval}
          hostName={plan.hosts.find(host => host.primary)?.user.firstName}
          openAppLink={`/plans/${plan.id}/app?open=1`}
        />
      </div>
    );
  }

  if (!request?.submittedAt) return null;

  const isPending = request.state === EventRequestState.Pending;
  const hostFirstName = plan.hosts.find(host => host.primary)?.user.firstName?.trim() || "The host";
  let title = isPending ? "Place requested" : "Request sent";

  let body = isPending
    ? `${hostFirstName}’s got it and usually replies within a day. We’ll let you know the second they do.`
    : "Your request is with the host.";

  if (request.state === EventRequestState.PendingWaitlist) {
    title = "You're on the waitlist";
    body = request.ticketPurchase
      ? `Your payment ${regionalCopy.authorization} has been released. You're still on the waitlist, and we'll let you know if a ${regionalCopy.place} opens up.`
      : `The host is reviewing your waitlist request. If they approve it, we'll let you know when a ${regionalCopy.place} opens up.`;
  } else if (request.state === EventRequestState.Approving) {
    title = regionalCopy.finalizingPayment;
    body = "The host approved your request. We're securing your ticket now.";
  } else if (request.state === EventRequestState.InAcceptedWaitlist) {
    title = `A ${regionalCopy.place} is available`;
    body = `Choose a current ticket and payment method to claim your ${regionalCopy.place}.`;
  } else if (
    request.state === EventRequestState.Declined ||
    request.state === EventRequestState.Expired
  ) {
    title = "This request wasn't approved";
    body = resolutionMessage(request.resolutionReason, regionalCopy);
  }

  return (
    <div className="mt-4 flex flex-col items-center gap-4 text-center">
      {isPending && (
        <img src={YellowFlowerImage} alt="" aria-hidden="true" className="size-20 object-contain" />
      )}
      <div className="w-full">
        <Text as={DialogTitle} variant="primary" size={isPending ? "md" : "sm"}>
          {title}
        </Text>
        <Text as="p" size="sm" className="mt-2 leading-relaxed">
          {body}
        </Text>

      </div>

      {request.ticketPurchaseCode?.code && (
        <div className="rounded-xl bg-butter-cream px-6 py-4">
          <Text as="p" variant="secondary" size="sm">
            Your app code
          </Text>
          <p className="font-mono text-base font-normal tracking-[0.2em] text-butter-green">
            {request.ticketPurchaseCode.code}
          </p>
        </div>
      )}
      {request.state === EventRequestState.InAcceptedWaitlist &&
        request.resolutionReason !== EventRequestResolutionReason.EventFull && (
          <PrimaryButton type="button" className="text-base!" onClick={onStartCheckout}>
            Choose a ticket
          </PrimaryButton>
        )}
      <div className="flex w-full flex-col gap-1">
        <PrimaryButton to={`/plans/${plan.id}/app?open=1`} className="text-base!">
          {isPending ? "Get the app" : "Open in the Butter app"}
        </PrimaryButton>
        <div>
          {error && <Text role="alert" size="sm" color="pink">{error}</Text>}
          <button
            type="button"
            disabled={refreshing}
            onClick={() => void onRefresh()}
            className="min-h-11 font-sans text-sm text-butter-green underline underline-offset-4 disabled:opacity-40"
          >
            {refreshing ? "Checking…" : error ? "Try again" : "Check for updates"}
          </button>
        </div>
      </div>
    </div>
  );
}

function resolutionMessage(
  reason: EventRequestResolutionReason | null | undefined,
  regionalCopy: ReturnType<typeof getRegionalCopy>,
) {
  switch (reason) {
    case EventRequestResolutionReason.PaymentFailed:
    case EventRequestResolutionReason.PaymentCancelled:
      return `Your payment ${regionalCopy.authorization} couldn't be completed. You can start a fresh checkout.`;
    case EventRequestResolutionReason.VariationSoldOut:
      return "That ticket type sold out before approval. You can choose another available ticket.";
    case EventRequestResolutionReason.EventFull:
      return "The plan filled up before approval.";
    case EventRequestResolutionReason.ApprovalDeadline:
      return `The host didn't respond before the decision deadline, so the ${regionalCopy.authorization} was released.`;
    default:
      return `The ${regionalCopy.authorization} was released and you weren't charged.`;
  }
}
